import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  ClipboardCheck, Plus, Search, ChevronRight, ArrowLeft,
  AlertTriangle, Warehouse, X, Lock, Pencil, Trash2, Check, RefreshCw
} from 'lucide-react';

import { toast } from 'sonner';
import * as api from '../api';
import { NouvelInventairePanel, InventaireDetail } from './StocksInventaire';
import { useAuth } from '../context/AuthContext';

// ─── Sélecteur de stock ────────────────────────────────────────────────────────

function StockSelectorModal({ stocks, onSelect, onClose }) {
  const [q, setQ] = useState('');
  const filtered = stocks.filter(s =>
    !q.trim() || s.nom.toLowerCase().includes(q.toLowerCase())
  );
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)' }}>
      <div className="w-full max-w-md rounded-2xl shadow-2xl bg-white overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: 'var(--border)' }}>
          <h2 className="text-base font-bold" style={{  color: 'var(--text)' }}>
            Choisir un dépôt
          </h2>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100">
            <X size={16} style={{ color: 'var(--text-muted)' }} />
          </button>
        </div>
        <div className="px-4 py-3 border-b" style={{ borderColor: 'var(--border-light)' }}>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-muted)' }} />
            <input
              autoFocus
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder="Rechercher un dépôt…"
              className="w-full pl-8 pr-3 py-2 rounded-lg text-sm border outline-none"
              style={{ borderColor: 'var(--border)', color: 'var(--text)', background: 'var(--bg-input)' }}
            />
          </div>
        </div>
        <div className="overflow-y-auto max-h-64 px-2 py-2 space-y-1">
          {filtered.length === 0 ? (
            <p className="text-xs text-center py-6" style={{ color: 'var(--text-muted)' }}>Aucun dépôt trouvé</p>
          ) : filtered.map(s => (
            <button key={s.stock_id} onClick={() => onSelect(s)}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-gray-50 text-left transition-colors">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ background: 'var(--primary-50)' }}>
                <Warehouse size={14} style={{ color: 'var(--primary-solid)' }} />
              </div>
              <span className="text-sm font-medium" style={{ color: 'var(--text)' }}>{s.nom}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Page principale ───────────────────────────────────────────────────────────

function TabInventaire() {
  const { activeAgences } = useAuth();
  const [sessions, setSessions]               = useState([]);
  const [stocks, setStocks]                   = useState([]);
  const [loading, setLoading]                 = useState(true);
  const [selectedId, setSelectedId]           = useState(null);
  const [search, setSearch]                   = useState('');

  // Création
  const [showStockSelector, setShowStockSelector] = useState(false);
  const [selectedStock, setSelectedStock]         = useState(null);
  const [stockZones, setStockZones]               = useState([]);
  const [stockArticles, setStockArticles]         = useState([]);
  const [loadingStockData, setLoadingStockData]   = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const agenceIds = activeAgences?.map(a => a.agence_id).filter(Boolean) || [];
      const [sessionsRes, stocksRes] = await Promise.all([
        api.getInventaires(null, agenceIds.length ? agenceIds : null),
        api.getStocks(),
      ]);
      setSessions(sessionsRes.data || []);
      // Filtrer les dépôts selon les agences actives (pour le sélecteur "Nouvel inventaire")
      const allStocks = Array.isArray(stocksRes.data) ? stocksRes.data : [];
      setStocks(agenceIds.length ? allStocks.filter(s => agenceIds.includes(s.agence_id)) : allStocks);
    } catch { toast.error('Erreur chargement des inventaires'); }
    finally { setLoading(false); }
  }, [activeAgences]);

  useEffect(() => { load(); }, [load]);

  const handleSelectStock = async (stock) => {
    setShowStockSelector(false);
    setSelectedStock(stock);
    setLoadingStockData(true);
    try {
      const [stockRes, articlesRes] = await Promise.all([
        api.getStock(stock.stock_id),
        api.getStockArticles(stock.stock_id),
      ]);
      setStockZones(stockRes.data?.zones || []);
      setStockArticles(Array.isArray(articlesRes.data) ? articlesRes.data : []);
    } catch { toast.error('Erreur chargement des données du dépôt'); setSelectedStock(null); }
    finally { setLoadingStockData(false); }
  };

  const handleCreated = (session) => {
    setSessions(prev => [session, ...prev]);
    setSelectedStock(null);
    setSelectedId(session.session_id);
  };

  const handleUpdated = (updated) => {
    setSessions(prev => prev.map(s => s.session_id === updated.session_id ? { ...s, ...updated } : s));
  };

  const handleDelete = async (sessionId) => {
    try {
      await api.deleteInventaire(sessionId);
      setSessions(prev => prev.filter(s => s.session_id !== sessionId));
      if (selectedId === sessionId) setSelectedId(null);
      toast.success('Session supprimée');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur suppression');
    }
  };

  // ── Vue détail ─────────────────────────────────────────────────────────────
  if (selectedId) {
    return (
      <div className="px-0 py-1 sm:px-6 sm:py-6 max-w-4xl mx-auto">
        <button onClick={() => setSelectedId(null)} data-testid="back-to-inventaires-btn"
          className="flex items-center gap-1.5 text-sm mb-5 hover:underline"
          style={{ color: 'var(--text-muted)' }}>
          <ArrowLeft size={14} /> Retour aux inventaires
        </button>
        <InventaireDetail sessionId={selectedId} onBack={() => setSelectedId(null)} onUpdated={handleUpdated} />
      </div>
    );
  }

  // ── Filtrage ───────────────────────────────────────────────────────────────
  const filtered = sessions.filter(s => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (s.titre || '').toLowerCase().includes(q) || (s.stock_nom || '').toLowerCase().includes(q);
  });

  const nbEnCours  = sessions.filter(s => s.statut === 'en_cours').length;
  const nbClotures = sessions.filter(s => s.statut === 'cloturee').length;

  const familles = [...new Set(stockArticles.map(a => a.famille).filter(Boolean))].sort();

  // ── Rendu liste ────────────────────────────────────────────────────────────
  return (
    <div className="px-6 py-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold" style={{  color: 'var(--text)' }}>
            Inventaires
          </h1>
          <p className="text-sm mt-0.5" style={{ color: 'var(--text-muted)' }}>
            {sessions.length} session{sessions.length !== 1 ? 's' : ''} · {nbEnCours} en cours · {nbClotures} clôturée{nbClotures !== 1 ? 's' : ''}
          </p>
        </div>
        <button onClick={() => setShowStockSelector(true)} data-testid="new-inventaire-global-btn"
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-white"
          style={{ background: 'var(--primary-gradient)' }}>
          <Plus size={16} /> Nouvel inventaire
        </button>
      </div>

      {/* Alerte sessions en cours */}
      {nbEnCours > 0 && (
        <div className="flex items-start gap-2 p-3 rounded-xl text-sm mb-4"
          style={{ background: '#FEF3C7', border: '1px solid #FDE68A', color: '#92400E' }}>
          <AlertTriangle size={15} className="mt-0.5 flex-shrink-0" />
          <p>{nbEnCours} inventaire{nbEnCours > 1 ? 's' : ''} en cours — les mouvements de stock sont déconseillés pendant cette période.</p>
        </div>
      )}

      {/* Recherche */}
      <div className="relative mb-4">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-muted)' }} />
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Rechercher par titre ou dépôt…"
          className="w-full pl-8 pr-3 py-2.5 rounded-xl text-sm border outline-none"
          style={{ borderColor: 'var(--border)', color: 'var(--text)', background: 'white' }}
        />
      </div>

      {/* Liste */}
      {loading ? (
        <div className="flex justify-center py-16">
          <div className="animate-spin w-8 h-8 border-2 border-t-transparent rounded-full"
            style={{ borderColor: 'var(--primary-solid)', borderTopColor: 'transparent' }} />
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center rounded-2xl border"
          style={{ borderColor: 'var(--border-light)', background: '#FAFAFA' }}>
          <ClipboardCheck size={40} className="mb-3" style={{ color: 'var(--text-muted)' }} />
          <p className="text-sm font-semibold mb-1" style={{ color: 'var(--text-muted)' }}>
            {search ? 'Aucun résultat' : 'Aucun inventaire'}
          </p>
          {!search && (
            <p className="text-xs" style={{ color: 'var(--text-placeholder)' }}>
              Créez votre première session avec le bouton "Nouvel inventaire"
            </p>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(s => {
            const pct     = s.nb_total > 0 ? Math.round((s.nb_saisi / s.nb_total) * 100) : 0;
            const isActive = s.statut === 'en_cours';
            return (
              <div key={s.session_id} data-testid={`inventaire-row-${s.session_id}`}
                onClick={() => setSelectedId(s.session_id)}
                className="rounded-xl border p-4 cursor-pointer hover:shadow-sm transition-all bg-white"
                style={{ borderColor: isActive ? 'var(--primary-solid)' : 'var(--border-light)' }}>
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-0.5">
                      <span className="text-sm font-bold" style={{ color: 'var(--text)',  }}>
                        {s.titre}
                      </span>
                      {isActive
                        ? <span className="text-xs px-2 py-0.5 rounded-full font-semibold" style={{ background: '#FEF3C7', color: '#D97706' }}>En cours</span>
                        : <span className="text-xs px-2 py-0.5 rounded-full font-semibold" style={{ background: '#F0FDF4', color: '#16A34A' }}>Clôturé</span>}
                      {s.regularise && (
                        <span className="text-xs px-2 py-0.5 rounded-full font-semibold" style={{ background: '#EFF6FF', color: '#2563EB' }}>Régularisé</span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-xs" style={{ color: 'var(--text-muted)' }}>
                      <span className="flex items-center gap-1">
                        <Warehouse size={11} />{s.stock_nom || '—'}
                      </span>
                      <span>{new Date(s.date_creation).toLocaleDateString('fr-FR')}</span>
                      {s.nb_ecart > 0 && (
                        <span style={{ color: '#D97706' }}>{s.nb_ecart} écart{s.nb_ecart > 1 ? 's' : ''}</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {isActive && (
                      <button
                        data-testid={`delete-session-${s.session_id}`}
                        onClick={e => {
                          e.stopPropagation();
                          if (window.confirm('Supprimer cette session d\'inventaire ? Cette action est irréversible.')) {
                            handleDelete(s.session_id);
                          }
                        }}
                        className="p-1.5 rounded-lg transition-colors hover:bg-red-50"
                        style={{ color: '#DC2626' }}
                        title="Supprimer la session">
                        <Trash2 size={14} />
                      </button>
                    )}
                    <span className="text-sm font-bold" style={{ color: 'var(--primary-solid)' }}>{pct}%</span>
                    <ChevronRight size={15} style={{ color: 'var(--text-muted)' }} />
                  </div>
                </div>
                <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--border-light)' }}>
                  <div style={{
                    width: `${pct}%`,
                    height: '100%',
                    background: s.nb_ecart > 0 ? '#F59E0B' : 'var(--primary-solid)',
                    transition: 'width 0.3s',
                  }} />
                </div>
                <p className="text-xs mt-1.5" style={{ color: 'var(--text-muted)' }}>
                  {s.nb_saisi} / {s.nb_total} articles saisis
                </p>
              </div>
            );
          })}
        </div>
      )}

      {/* Sélecteur de stock */}
      {showStockSelector && (
        <StockSelectorModal
          stocks={stocks}
          onSelect={handleSelectStock}
          onClose={() => setShowStockSelector(false)}
        />
      )}

      {/* Chargement données stock */}
      {loadingStockData && (
        <div className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ background: 'rgba(0,0,0,0.3)' }}>
          <div className="bg-white rounded-2xl px-8 py-6 flex items-center gap-3 shadow-2xl">
            <div className="animate-spin w-5 h-5 border-2 border-t-transparent rounded-full"
              style={{ borderColor: 'var(--primary-solid)', borderTopColor: 'transparent' }} />
            <span className="text-sm font-medium" style={{ color: 'var(--text)' }}>Chargement du dépôt…</span>
          </div>
        </div>
      )}

      {/* Panel création */}
      {selectedStock && !loadingStockData && (
        <NouvelInventairePanel
          open={true}
          onClose={() => setSelectedStock(null)}
          stockId={selectedStock.stock_id}
          stockNom={selectedStock.nom}
          zones={stockZones}
          familles={familles}
          onCreated={handleCreated}
        />
      )}
    </div>
  );
}


const TABS = [
  { key: 'inventaire',  label: 'Inventaire'     },
  { key: 'ajustements', label: 'Ajustements'     },
  { key: 'motifs',      label: 'Motifs de régul' },
];

const fmtQty = (n) => n == null ? '—' : Number(n).toLocaleString('fr-FR', { maximumFractionDigits: 3 });

// ─── Onglet Motifs ────────────────────────────────────────────────────────────
function TabMotifs() {
  const [motifs, setMotifs]     = useState([]);
  const [editId, setEditId]     = useState(null);
  const [editVal, setEditVal]   = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.getMotisAjustement(); setMotifs(r.data || []); }
    catch { toast.error('Erreur chargement des motifs'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!newLabel.trim()) return;
    setSaving(true);
    try {
      const r = await api.createMotifAjustement({ libelle: newLabel.trim() });
      setMotifs(prev => [...prev, r.data]);
      setNewLabel('');
      toast.success('Motif créé');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur création'); }
    finally { setSaving(false); }
  };

  const handleUpdate = async (id) => {
    if (!editVal.trim()) return;
    setSaving(true);
    try {
      const r = await api.updateMotifAjustement(id, { libelle: editVal.trim() });
      setMotifs(prev => prev.map(m => m.motif_id === id ? r.data : m));
      setEditId(null);
      toast.success('Motif mis à jour');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur mise à jour'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce motif ?')) return;
    try {
      await api.deleteMotifAjustement(id);
      setMotifs(prev => prev.filter(m => m.motif_id !== id));
      toast.success('Motif supprimé');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur suppression'); }
  };

  return (
    <div className="max-w-lg space-y-4">
      <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
        Ces motifs apparaissent dans l'onglet Ajustements pour qualifier chaque régularisation d'inventaire.
      </p>
      <div className="bg-white rounded-2xl border overflow-hidden" style={{ borderColor: 'var(--border)' }}>
        {loading ? (
          <div className="p-8 text-center text-sm" style={{ color: 'var(--text-muted)' }}>Chargement…</div>
        ) : (
          <>
            {motifs.map((m, i) => (
              <div key={m.motif_id} className="flex items-center gap-3 px-4 py-3"
                style={{ borderBottom: i < motifs.length - 1 ? '1px solid var(--border-light)' : 'none' }}>
                {editId === m.motif_id ? (
                  <>
                    <input autoFocus value={editVal} onChange={e => setEditVal(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') handleUpdate(m.motif_id); if (e.key === 'Escape') setEditId(null); }}
                      className="flex-1 px-3 py-1.5 rounded-lg border text-sm"
                      style={{ borderColor: 'var(--primary-solid)', outline: 'none' }} />
                    <button onClick={() => handleUpdate(m.motif_id)} disabled={saving}
                      className="p-1.5 rounded-lg hover:bg-green-50 text-green-600"><Check size={14} /></button>
                    <button onClick={() => setEditId(null)}
                      className="p-1.5 rounded-lg hover:bg-gray-100" style={{ color: 'var(--text-muted)' }}><X size={14} /></button>
                  </>
                ) : (
                  <>
                    <span className="flex-1 text-sm" style={{ color: 'var(--text)' }}>{m.libelle}</span>
                    <button onClick={() => { setEditId(m.motif_id); setEditVal(m.libelle); }}
                      className="p-1.5 rounded-lg hover:bg-gray-100" style={{ color: 'var(--text-muted)' }}><Pencil size={13} /></button>
                    <button onClick={() => handleDelete(m.motif_id)}
                      className="p-1.5 rounded-lg hover:bg-red-50 text-red-500"><Trash2 size={13} /></button>
                  </>
                )}
              </div>
            ))}
            {/* Ghost row */}
            <div className="flex items-center gap-3 px-4 py-3"
              style={{ borderTop: motifs.length ? '1px solid var(--border-light)' : 'none', background: '#FAFAFA' }}>
              <input value={newLabel} onChange={e => setNewLabel(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleCreate()}
                placeholder="Nouveau motif…"
                className="flex-1 px-3 py-1.5 rounded-lg border text-sm bg-white"
                style={{ borderColor: 'var(--border)', color: 'var(--text)' }} />
              <button onClick={handleCreate} disabled={saving || !newLabel.trim()}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold text-white disabled:opacity-40"
                style={{ background: 'var(--primary-solid)' }}>
                <Plus size={13} /> Ajouter
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Onglet Ajustements ───────────────────────────────────────────────────────
function TabAjustements() {
  const [motifs, setMotifs]       = useState([]);
  const [lignes, setLignes]       = useState([]);
  const [query, setQuery]         = useState('');
  const [results, setResults]     = useState([]);
  const [searching, setSearching] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const debounce = useRef(null);

  useEffect(() => {
    api.getMotisAjustement().then(r => setMotifs(r.data || [])).catch(() => {});
  }, []);

  const handleSearch = useCallback(async (q) => {
    if (!q.trim()) { setResults([]); return; }
    setSearching(true);
    try { const r = await api.searchStockArticles(q); setResults(r.data || []); }
    catch { setResults([]); }
    finally { setSearching(false); }
  }, []);

  const onQueryChange = (e) => {
    const v = e.target.value;
    setQuery(v);
    clearTimeout(debounce.current);
    debounce.current = setTimeout(() => handleSearch(v), 300);
  };

  const addLigne = (article) => {
    if (lignes.find(l => l.entry_id === article.entry_id)) {
      toast.info('Cette référence est déjà dans la liste'); return;
    }
    setLignes(prev => [...prev, { ...article, nouvelle_quantite: '', motif_id: '', motif_libelle: '' }]);
    setQuery(''); setResults([]);
  };

  const updateLigne = (entry_id, field, value) => {
    setLignes(prev => prev.map(l => {
      if (l.entry_id !== entry_id) return l;
      if (field === 'motif_id') {
        const m = motifs.find(x => x.motif_id === value);
        return { ...l, motif_id: value, motif_libelle: m?.libelle || '' };
      }
      return { ...l, [field]: value };
    }));
  };

  const removeLigne = (entry_id) => setLignes(prev => prev.filter(l => l.entry_id !== entry_id));

  const handleSubmit = async () => {
    const valides = lignes.filter(l => l.nouvelle_quantite !== '' && l.nouvelle_quantite != null);
    if (!valides.length) { toast.error('Aucune nouvelle quantité saisie'); return; }
    const grossDelta = valides.filter(l => {
      const curr = parseFloat(l.quantite_physique ?? l.quantite_disponible) || 0;
      const nv   = parseFloat(l.nouvelle_quantite)   || 0;
      return curr > 0 && Math.abs(nv - curr) / curr > 0.5;
    });
    if (grossDelta.length > 0 && !window.confirm(`${grossDelta.length} ligne(s) ont un écart supérieur à 50%.\nConfirmer ?`)) return;

    setSubmitting(true);
    let ok = 0;
    for (const l of valides) {
      try {
        await api.ajusterStockArticle(l.stock_id, {
          article_id: l.article_id, zone_id: l.zone_id || null,
          quantite: parseFloat(l.nouvelle_quantite), type_mouvement: 'ajustement',
          commentaire: l.motif_libelle || null,
          motif_id: l.motif_id || null, motif_libelle: l.motif_libelle || null,
        });
        ok++;
      } catch (e) { toast.error(`${l.article_ref} : ${e.response?.data?.detail || 'Erreur'}`); }
    }
    setSubmitting(false);
    if (ok > 0) {
      toast.success(`${ok} ajustement(s) appliqué(s)`);
      setLignes(prev => prev.filter(l => !valides.find(v => v.entry_id === l.entry_id)));
    }
  };

  return (
    <div className="space-y-5">
      {/* Recherche */}
      <div className="relative max-w-sm">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-muted)' }} />
        <input value={query} onChange={onQueryChange}
          placeholder="Rechercher une référence article…"
          className="w-full pl-8 pr-8 py-2 rounded-xl border text-sm"
          style={{ borderColor: 'var(--border)', background: '#fff', color: 'var(--text)' }} />
        {searching && <RefreshCw size={12} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin" style={{ color: 'var(--text-muted)' }} />}
        {results.length > 0 && (
          <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-xl border shadow-lg z-20 max-h-64 overflow-y-auto" style={{ borderColor: 'var(--border)' }}>
            {results.map(r => (
              <button key={r.entry_id} onClick={() => addLigne(r)}
                className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-blue-50 text-left border-b last:border-0"
                style={{ borderColor: 'var(--border-light)' }}>
                <div className="flex-1 min-w-0">
                  <span className="font-semibold text-sm" style={{ color: 'var(--text)' }}>{r.article_ref}</span>
                  <span className="ml-2 text-xs" style={{ color: 'var(--text-muted)' }}>{r.article_nom}</span>
                </div>
                <div className="text-xs text-right" style={{ color: 'var(--text-secondary)' }}>
                  <div>{r.stock_nom}</div>
                  {r.zone_nom && <div>{r.zone_nom}</div>}
                </div>
                <span className="text-xs font-bold ml-2 whitespace-nowrap" style={{ color: 'var(--primary-solid)' }}>
                  {fmtQty(r.quantite_physique ?? r.quantite_disponible)}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Table d'ajustements */}
      {lignes.length > 0 ? (
        <>
          <div className="bg-white rounded-2xl border overflow-hidden" style={{ borderColor: 'var(--border)' }}>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ background: '#FAFAFA', borderBottom: '1px solid var(--border)' }}>
                    {['Référence', 'Désignation', 'Stock / Zone', 'Qté actuelle', 'Nouvelle qté', 'Motif', ''].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide whitespace-nowrap"
                        style={{ color: 'var(--text-muted)' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {lignes.map((l, i) => {
                    const curr = parseFloat(l.quantite_physique ?? l.quantite_disponible) || 0;
                    const nv   = parseFloat(l.nouvelle_quantite);
                    const delta = !isNaN(nv) ? nv - curr : null;
                    const gros  = delta != null && curr > 0 && Math.abs(delta) / curr > 0.5;
                    return (
                      <tr key={l.entry_id} style={{ borderBottom: i < lignes.length - 1 ? '1px solid var(--border-light)' : 'none' }}>
                        <td className="px-4 py-3 font-semibold text-xs" style={{ color: 'var(--text)' }}>{l.article_ref}</td>
                        <td className="px-4 py-3 text-xs" style={{ color: 'var(--text-secondary)' }}>{l.article_nom}</td>
                        <td className="px-4 py-3 text-xs" style={{ color: 'var(--text-muted)' }}>
                          <div>{l.stock_nom}</div>
                          {l.zone_nom && <div className="opacity-70">{l.zone_nom}</div>}
                        </td>
                        <td className="px-4 py-3 font-semibold text-xs" style={{ color: 'var(--text)' }}>{fmtQty(curr)}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <input type="number" min="0" step="0.001"
                              value={l.nouvelle_quantite}
                              onChange={e => updateLigne(l.entry_id, 'nouvelle_quantite', e.target.value)}
                              placeholder="—"
                              className="w-24 px-2 py-1.5 rounded-lg border text-sm text-center font-semibold"
                              style={{ borderColor: gros ? '#FBBF24' : 'var(--border)', background: gros ? '#FFFBEB' : '#fff' }} />
                            {delta != null && (
                              <span className="text-xs font-medium"
                                style={{ color: delta > 0 ? '#16A34A' : delta < 0 ? '#DC2626' : 'var(--text-muted)' }}>
                                {delta > 0 ? `+${fmtQty(delta)}` : fmtQty(delta)}
                              </span>
                            )}
                            {gros && <AlertTriangle size={13} className="text-amber-500" />}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <select value={l.motif_id}
                            onChange={e => updateLigne(l.entry_id, 'motif_id', e.target.value)}
                            className="px-2 py-1.5 rounded-lg border text-sm bg-white"
                            style={{ borderColor: 'var(--border)', minWidth: '160px' }}>
                            <option value="">— Sans motif</option>
                            {motifs.map(m => <option key={m.motif_id} value={m.motif_id}>{m.libelle}</option>)}
                          </select>
                        </td>
                        <td className="px-4 py-3">
                          <button onClick={() => removeLigne(l.entry_id)}
                            className="p-1.5 rounded-lg hover:bg-red-50 text-red-400"><X size={14} /></button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex items-center justify-between flex-wrap gap-3">
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
              {lignes.filter(l => l.nouvelle_quantite !== '').length} / {lignes.length} ligne(s) avec nouvelle quantité
            </p>
            <button data-testid="btn-mettre-a-jour-inventaire" onClick={handleSubmit}
              disabled={submitting || !lignes.some(l => l.nouvelle_quantite !== '')}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-white disabled:opacity-40"
              style={{ background: 'var(--primary-solid)' }}>
              {submitting ? <RefreshCw size={14} className="animate-spin" /> : <Check size={14} />}
              Mettre à jour l'inventaire
            </button>
          </div>
        </>
      ) : (
        <div className="py-16 text-center rounded-2xl border-2 border-dashed" style={{ borderColor: 'var(--border)' }}>
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
            Recherchez une référence article ci-dessus pour ajouter un ajustement
          </p>
        </div>
      )}
    </div>
  );
}

// ─── Page principale avec onglets ─────────────────────────────────────────────
export default function Inventaires() {
  const { user, menuRights, activeAgences } = useAuth();
  const isAdmin = user?.role === 'super_admin' || user?.role === 'admin_org';
  const rights  = menuRights || [];

  const visibleTabs = TABS.filter(t => {
    if (t.key === 'inventaire')  return true;  // toujours visible
    if (t.key === 'ajustements') return isAdmin || rights.includes('inventaires_ajustements');
    if (t.key === 'motifs')      return isAdmin || rights.includes('inventaires_motifs');
    return true;
  });

  const [tab, setTab] = useState('inventaire');

  // Si l'onglet actif n'est plus visible, revenir au premier visible
  const activeTab = visibleTabs.find(t => t.key === tab) ? tab : (visibleTabs[0]?.key || 'inventaire');

  return (
    <div className="min-h-screen p-6 space-y-5" style={{ background: 'var(--background)' }}>
      <div>
        <h1 className="text-2xl font-bold mb-4" style={{ color: 'var(--text)' }}>Inventaires</h1>
        <div className="flex gap-1 p-1 rounded-xl w-fit" style={{ background: 'var(--surface-alt, #F1F5F9)' }}>
          {visibleTabs.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              data-testid={`tab-inventaire-${t.key}`}
              className="px-4 py-2 rounded-lg text-sm font-semibold transition-all"
              style={{
                background: activeTab === t.key ? '#fff' : 'transparent',
                color:      activeTab === t.key ? 'var(--text)' : 'var(--text-muted)',
                boxShadow:  activeTab === t.key ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
              }}>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {activeTab === 'inventaire'  && <TabInventaire />}
      {activeTab === 'ajustements' && <TabAjustements />}
      {activeTab === 'motifs'      && <TabMotifs />}
    </div>
  );
}
